/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Edit2, Save, X, MapPin, Calendar, User, Phone, Mail,
  AlertTriangle, Plus, Trash2, FileText, Image, Clock, History, Wrench, Package,
  Euro, RefreshCw, Receipt, ChevronLeft, ChevronRight, Printer
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import SessioneAdminModal from '../components/SessioneAdminModal';
import FatturazioniIntervento from '../components/FatturazioniIntervento';
import ResocontoButton from '../components/ResocontoButton';
import { apriRegistro } from '../utils/resoconto';
import { UNITA, periodoDi, sposta, etichetta, fmtQuando } from '../utils/periodo';
import { interventiAPI } from '../api/interventi';
import { utentiAPI } from '../api/utenti';
import { fornitoriAPI } from '../api/fornitori';
import { fmtDataOra, relativeTime, fmtDataInput } from '../utils/date';
import { fmtDurata, fmtDurataOre, secondiSessione, secondiTotaliSessioni } from '../utils/durata';

const ACCENT = '#8B5CF6';

const PRIORITA_STYLE = {
  urgente: { bg: '#FEE2E2', color: '#991B1B', label: 'Urgente' },
  alta:    { bg: '#FED7AA', color: '#9A3412', label: 'Alta' },
  normale: { bg: '#E0E7FF', color: '#3730A3', label: 'Normale' },
  bassa:   { bg: '#F3F4F6', color: '#4B5563', label: 'Bassa' },
};

const STATO_LABELS = {
  in_attesa: 'In attesa', confermato: 'Confermato',
  in_corso: 'In corso', completato: 'Completato', annullato: 'Annullato',
};

const CATEGORIE_INTERNE = { magazzino: 'Magazzino', manutenzione_automezzi: 'Manutenzione automezzi', formazione: 'Formazione', riunione: 'Riunione', altro: 'Altro' };

const TABS = [
  { id: 'info',       label: 'Info',       icon: FileText },
  { id: 'tecnici',    label: 'Tecnici',    icon: User },
  { id: 'materiali',  label: 'Materiali',  icon: Package },
  { id: 'conto',      label: 'Conto',      icon: Euro },
  { id: 'files',      label: 'Foto/Files', icon: Image },
  { id: 'sessioni',   label: 'Sessioni',   icon: Clock },
  { id: 'log',        label: 'Cronologia', icon: History },
];

export default function InterventoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('info');

  const { data: i, isLoading, error } = useQuery({
    queryKey: ['intervento', id],
    queryFn: () => interventiAPI.detail(id),
  });

  const { data: stati = [] } = useQuery({
    queryKey: ['interventi-stati'],
    queryFn: () => interventiAPI.stati(),
  });

  const changeStatoMutation = useMutation({
    mutationFn: (stato) => interventiAPI.changeStato(id, stato),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['intervento', id] });
      queryClient.invalidateQueries({ queryKey: ['interventi'] });
      queryClient.invalidateQueries({ queryKey: ['interventi-stats'] });
    },
  });

  if (isLoading) {
    return <AdminLayout title="Dettaglio intervento"><div style={styles.loading}>Caricamento...</div></AdminLayout>;
  }
  if (error || !i) {
    return (
      <AdminLayout title="Dettaglio intervento">
        <button onClick={() => navigate('/interventi')} style={styles.backBtn}>
          <ArrowLeft size={16} /> Torna alla lista
        </button>
        <div style={styles.errorCard}>
          <h3>Intervento non trovato</h3>
          <p style={{ color: '#6B7280' }}>{error?.response?.data?.error || 'Verifica che l\'ID sia corretto.'}</p>
        </div>
      </AdminLayout>
    );
  }

  const prio = PRIORITA_STYLE[i.priorita] || PRIORITA_STYLE.normale;

  return (
    <AdminLayout title={i.codice}>
      <button onClick={() => navigate('/interventi')} style={styles.backBtn}>
        <ArrowLeft size={16} /> Torna alla lista
      </button>

      {/* Header */}
      <div style={styles.headerCard}>
        <div style={styles.headerTop}>
          <div style={{ flex: 1 }}>
            <div style={styles.badgesRow}>
              <span style={{ ...styles.statoBadge, backgroundColor: i.stato_colore + '22', color: i.stato_colore }}>
                {STATO_LABELS[i.stato] || i.stato}
              </span>
              <span style={{ ...styles.prioBadge, backgroundColor: prio.bg, color: prio.color }}>
                {i.priorita === 'urgente' && <AlertTriangle size={12} />} {prio.label}
              </span>
              <code style={styles.codice}>{i.codice}</code>
            </div>
            <h1 style={styles.headerTitle}>{i.titolo}</h1>
            {i.tipo_problema && <div style={styles.tipoProblema}>Tipo: {i.tipo_problema}</div>}
          </div>

          {/* Selettore stato */}
          <div style={styles.statoSelector}>
            <label style={styles.statoLabel}>Cambia stato:</label>
            <select
              value={i.stato}
              onChange={(e) => changeStatoMutation.mutate(e.target.value)}
              style={{ ...styles.statoSelect, borderColor: i.stato_colore, color: i.stato_colore }}
            >
              {stati.map((s) => (
                <option key={s.nome} value={s.nome}>{STATO_LABELS[s.nome] || s.nome}</option>
              ))}
            </select>
            <div style={{ marginTop: '0.6rem', textAlign: 'right' }}>
              <ResocontoButton interventoId={i.id} codice={i.codice} />
            </div>
          </div>
        </div>

        <div style={styles.headerMeta}>
          {i.cliente_id ? (
            <div><User size={14} /> {i.cliente_nome} {i.cliente_cognome}</div>
          ) : (
            <div style={styles.internoBadge}>Lavoro interno{i.categoria_interna ? ` · ${CATEGORIE_INTERNE[i.categoria_interna] || i.categoria_interna}` : ''}</div>
          )}
          {i.cliente_telefono && <div><Phone size={14} /> {i.cliente_telefono}</div>}
          {i.cliente_email && <div><Mail size={14} /> {i.cliente_email}</div>}
          {(i.indirizzo || i.citta) && (
            <div><MapPin size={14} /> {[i.indirizzo, i.citta, i.cap].filter(Boolean).join(', ')}</div>
          )}
          <div><Calendar size={14} /> Richiesto: {fmtDataOra(i.data_richiesta)}</div>
          {i.data_appuntamento && <div><Calendar size={14} /> Appuntamento: {fmtDataOra(i.data_appuntamento)}</div>}
        </div>
      </div>

      {i.prodotto_cliente_id && (
        <div style={styles.manutBox}>
          <div style={styles.manutHead}>
            <Wrench size={16} color="#8B5CF6" />
            <span style={styles.manutTitle}>Manutenzione collegata</span>
          </div>
          <div style={styles.manutBody}>
            <div>
              <div style={styles.manutProdotto}>{[i.prod_tipo, i.prod_marca, i.prod_modello].filter(Boolean).join(' ')}</div>
              <div style={styles.manutMeta}>
                {i.manutenzione_nome}{i.prod_ubicazione ? ` · ${i.prod_ubicazione}` : ''}
              </div>
            </div>
            <button onClick={() => navigate(`/prodotti?cliente=${i.cliente_id}`)} style={styles.manutBtn}>
              <Package size={14} /> Vedi prodotti cliente
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={styles.tabsRow}>
        {TABS.map((t) => {
          const Icon = t.icon;
          const count = getTabCount(t.id, i);
          return (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              style={{
                ...styles.tabBtn,
                ...(activeTab === t.id ? styles.tabBtnActive : {}),
              }}
            >
              <Icon size={15} /> {t.label}
              {count !== null && <span style={styles.tabBadge}>{count}</span>}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div style={styles.tabContent}>
        {activeTab === 'info'      && <TabInfo intervento={i} onSaved={() => queryClient.invalidateQueries({ queryKey: ['intervento', id] })} />}
        {activeTab === 'tecnici'   && <TabTecnici intervento={i} onChanged={() => { queryClient.invalidateQueries({ queryKey: ['intervento', id] }); queryClient.invalidateQueries({ queryKey: ['interventi'] }); }} />}
        {activeTab === 'materiali' && <TabMateriali intervento={i} onChanged={() => queryClient.invalidateQueries({ queryKey: ['intervento', id] })} />}
        {activeTab === 'conto'     && <TabConto intervento={i} onRicalcolato={() => queryClient.invalidateQueries({ queryKey: ['intervento', id] })} />}
        {activeTab === 'files'     && <TabFiles intervento={i} />}
        {activeTab === 'sessioni'  && <TabSessioni intervento={i} onChanged={() => queryClient.invalidateQueries({ queryKey: ['intervento', id] })} />}
        {activeTab === 'log'       && <TabLog intervento={i} />}
      </div>
    </AdminLayout>
  );
}

/*
 * Il conto dell'intervento: come si e' formato il totale, riga per riga.
 *
 * Non e' un doppione della scheda Info: li' ci sono gli importi CONGELATI, cioe'
 * quelli con cui l'intervento e' stato chiuso. Qui c'e' il conto ricalcolato
 * adesso, con il dettaglio di dove nasce ogni euro. Servono tutti e due, e quando
 * non coincidono vuol dire che qualcosa e' cambiato dopo la chiusura.
 *
 * Il filtro in alto restringe il conto a un giorno, una settimana, un mese o un
 * anno: quante ore si sono fatte a marzo e quanto valgono. Sotto, le fatture per
 * periodo, per fatturare un cantiere lungo un mese alla volta.
 */
function TabConto({ intervento, onRicalcolato }) {
  const [unita, setUnita] = useState('totale');
  const [rif, setRif] = useState(() => new Date());
  const periodo = periodoDi(unita, rif);

  const { data: conto, isLoading, error } = useQuery({
    queryKey: ['conto-intervento', intervento.id, periodo?.dal || null, periodo?.al || null],
    queryFn: () => interventiAPI.prezzo(intervento.id, periodo),
    retry: false,
    placeholderData: (prima) => prima,
  });

  const ricalcola = useMutation({
    mutationFn: () => interventiAPI.ricalcolaPrezzo(intervento.id),
    onSuccess: onRicalcolato,
  });

  const eur = (v) => (v === null || v === undefined ? '—' : `€ ${Number(v).toFixed(2)}`);
  const ore = (v) => `${Number(v ?? 0).toFixed(2).replace('.', ',')} h`;

  const filtro = (
    <div style={styles.filtroBar}>
      <div style={styles.filtroUnita}>
        {UNITA.map((u) => (
          <button
            key={u.id}
            onClick={() => setUnita(u.id)}
            style={{ ...styles.filtroBtn, ...(unita === u.id ? styles.filtroBtnAttivo : {}) }}
          >
            {u.label}
          </button>
        ))}
      </div>
      {periodo && (
        <div style={styles.filtroNav}>
          <button style={styles.filtroFreccia} onClick={() => setRif(sposta(unita, rif, -1))} aria-label="Periodo precedente">
            <ChevronLeft size={16} />
          </button>
          <span style={styles.filtroEtichetta}>{etichetta(unita, periodo)}</span>
          <button style={styles.filtroFreccia} onClick={() => setRif(sposta(unita, rif, 1))} aria-label="Periodo successivo">
            <ChevronRight size={16} />
          </button>
          <button style={styles.filtroOggi} onClick={() => setRif(new Date())}>Oggi</button>
        </div>
      )}
    </div>
  );

  if (isLoading) return <div>{filtro}<div style={styles.empty}>Caricamento del conto...</div></div>;
  if (error) return <div>{filtro}<div style={styles.empty}>{error.response?.data?.error || 'Errore nel calcolo del conto'}</div></div>;

  const congelato = intervento.costo_totale === null || intervento.costo_totale === undefined
    ? null : parseFloat(intervento.costo_totale);
  const adesso = conto.costo_totale;
  // Il confronto con l'importo congelato ha senso solo sul conto intero.
  const diverso = !periodo && congelato !== null && adesso !== null && Math.abs(congelato - adesso) >= 0.01;
  const maxOre = Math.max(...conto.suddivisione.map((x) => x.ore), 0.0001);

  return (
    <div>
      {filtro}

      {periodo && (
        <div style={{ marginBottom: '0.9rem' }}>
          <button
            style={styles.btnRegistro}
            onClick={() => apriRegistro({ dal: periodo.dal, al: periodo.al, intervento: intervento.id, materiali: true, importi: true })
              .catch((e) => window.alert(e.message))}
          >
            <Printer size={15} /> Registro lavori del periodo (PDF)
          </button>
          <span style={styles.contoNota}> Giorni, orari, lavoro svolto e materiali: da allegare all'acconto.</span>
        </div>
      )}

      <div style={styles.oreRiassunto}>
        <div>
          <div style={styles.oreRiassuntoNum}>{ore(conto.ore_totali)}</div>
          <div style={styles.oreRiassuntoLabel}>
            ore lavorate {periodo ? `· ${etichetta(unita, periodo)}` : "in tutto l'intervento"}
          </div>
        </div>
        <div>
          <div style={styles.oreRiassuntoNum}>{eur(conto.costo_totale)}</div>
          <div style={styles.oreRiassuntoLabel}>valore, IVA esclusa</div>
        </div>
      </div>

      {conto.suddivisione.length > 1 && (
        <div style={{ marginBottom: '1.25rem' }}>
          <h3 style={styles.contoTitolo}>Ore {conto.suddivisione_per === 'mese' ? 'mese per mese' : 'giorno per giorno'}</h3>
          <div style={styles.barre}>
            {conto.suddivisione.map((x) => (
              <div key={x.quando} style={styles.barraRiga}>
                <span style={styles.barraQuando}>{fmtQuando(x.quando)}</span>
                <span style={styles.barraTraccia}>
                  <span style={{ ...styles.barraPiena, width: `${(x.ore / maxOre) * 100}%` }} />
                </span>
                <span style={styles.barraOre}>{ore(x.ore)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {conto.avvisi.length > 0 && (
        <div style={styles.avvisiBox}>
          <AlertTriangle size={16} />
          <div>
            <strong>Il totale e' incompleto:</strong>
            <ul style={styles.avvisiLista}>
              {conto.avvisi.map((a, k) => <li key={k}>{a}</li>)}
            </ul>
          </div>
        </div>
      )}

      {diverso && (
        <div style={styles.diversoBox}>
          <AlertTriangle size={16} />
          <div>
            L'intervento e' stato chiuso a <strong>{eur(congelato)}</strong> ma con i dati
            di adesso varrebbe <strong>{eur(adesso)}</strong>. Se la differenza e' voluta,
            ricalcola per aggiornare l'importo congelato.
          </div>
        </div>
      )}

      <h3 style={styles.contoTitolo}>Manodopera</h3>
      {conto.manodopera.length === 0 ? (
        <div style={styles.empty}>Nessun tecnico assegnato.</div>
      ) : (
        <table style={styles.contoTabella}>
          <thead>
            <tr>
              <th style={styles.contoTh}>Tecnico</th>
              <th style={{ ...styles.contoTh, textAlign: 'right' }}>Ore</th>
              <th style={{ ...styles.contoTh, textAlign: 'right' }}>Costo</th>
              <th style={{ ...styles.contoTh, textAlign: 'right' }}>Prezzo</th>
            </tr>
          </thead>
          <tbody>
            {conto.manodopera.map((r) => (
              <tr key={r.tecnico_id} style={styles.contoTr}>
                <td style={styles.contoTd}>
                  {r.nome}
                  {r.esterno ? (
                    <span style={styles.tagEsterno}>
                      esterno · {r.costo_orario === null ? 'costo non impostato' : `${r.costo_orario}/h`}
                      {r.ricarico_valore !== null && (r.ricarico_tipo === 'percentuale'
                        ? ` +${r.ricarico_valore}%`
                        : ` +${r.ricarico_valore}/h`)}
                    </span>
                  ) : (
                    <span style={styles.tagInterno}>interno · listino</span>
                  )}
                </td>
                <td style={{ ...styles.contoTd, textAlign: 'right' }}>{ore(r.ore)}</td>
                <td style={{ ...styles.contoTd, textAlign: 'right', color: '#6B7280' }}>{eur(r.costo)}</td>
                <td style={{ ...styles.contoTd, textAlign: 'right', fontWeight: 700 }}>{eur(r.prezzo)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3 style={styles.contoTitolo}>Totale</h3>
      <div style={styles.totaliBox}>
        <div style={styles.totaleRiga}><span>Manodopera</span><span>{eur(conto.prezzo_manodopera)}</span></div>
        <div style={styles.totaleRiga}><span>Materiali</span><span>{eur(conto.ricavo_materiali)}</span></div>
        {conto.rimborso_km > 0 && (
          <div style={styles.totaleRiga}><span>Rimborso chilometrico</span><span>{eur(conto.rimborso_km)}</span></div>
        )}
        {periodo && (
          <div style={{ ...styles.totaleRiga, color: '#6B7280', fontSize: '0.8rem' }}>
            <span>Il rimborso km e' dell'intervento intero: compare solo in Totale.</span>
          </div>
        )}
        <div style={{ ...styles.totaleRiga, ...styles.totaleFinale }}>
          <span>Totale, IVA esclusa</span><span>{eur(conto.costo_totale)}</span>
        </div>
        <div style={{ ...styles.totaleRiga, color: '#6B7280', fontSize: '0.85rem' }}>
          <span>Ci e' costato</span><span>{eur(conto.costo_sostenuto)}</span>
        </div>
        <div style={{ ...styles.totaleRiga, color: '#047857', fontWeight: 700 }}>
          <span>Margine{conto.margine_parziale ? ' (parziale)' : ''}</span><span>{eur(conto.margine)}</span>
        </div>
      </div>

      {conto.margine_parziale && (
        <div style={styles.notaMargine}>
          Il margine non tiene conto del costo delle ore dei tecnici interni, che questo
          sistema non registra: i 52 euro l'ora sono un prezzo di vendita, non un costo.
          Sui materiali e sugli artigiani esterni invece il margine e' completo.
        </div>
      )}

      {!periodo && (
        <div style={styles.contoAzioni}>
          <button
            onClick={() => ricalcola.mutate()}
            disabled={ricalcola.isPending}
            style={styles.ricalcolaBtn}
          >
            <RefreshCw size={15} /> {ricalcola.isPending ? 'Ricalcolo...' : 'Ricalcola e salva'}
          </button>
          <span style={styles.contoNota}>
            Riscrive gli importi congelati dell'intervento. Resta in cronologia.
          </span>
        </div>
      )}

      <FatturazioniIntervento intervento={intervento} periodo={periodo} />
    </div>
  );
}

function getTabCount(tabId, i) {
  if (tabId === 'tecnici')   return i.tecnici?.length || 0;
  if (tabId === 'materiali') return i.materiali?.length || 0;
  if (tabId === 'files')     return i.files?.length || 0;
  if (tabId === 'sessioni')  return i.sessioni?.length || 0;
  if (tabId === 'log')       return i.log?.length || 0;
  return null;
}

// ============================================================
// TAB: Info (modificabile)
// ============================================================
function TabInfo({ intervento, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    titolo: intervento.titolo || '',
    descrizione: intervento.descrizione || '',
    tipo_problema: intervento.tipo_problema || '',
    priorita: intervento.priorita || 'normale',
    indirizzo: intervento.indirizzo || '',
    citta: intervento.citta || '',
    cap: intervento.cap || '',
    data_appuntamento: fmtDataInput(intervento.data_appuntamento),
    note_interne: intervento.note_interne || '',
    note_cliente: intervento.note_cliente || '',
    costo_manodopera: intervento.costo_manodopera || '',
    costo_materiali: intervento.costo_materiali || '',
    costo_totale: intervento.costo_totale || '',
  });
  const [error, setError] = useState('');

  const updateMutation = useMutation({
    mutationFn: (data) => interventiAPI.update(intervento.id, data),
    onSuccess: () => { setEditing(false); onSaved(); },
    onError: (err) => setError(err.response?.data?.error || 'Errore nel salvataggio'),
  });

  const handle = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  const save = () => {
    setError('');
    updateMutation.mutate({
      ...form,
      data_appuntamento: form.data_appuntamento || null,
      costo_manodopera: form.costo_manodopera === '' ? null : parseFloat(form.costo_manodopera),
      costo_materiali: form.costo_materiali === '' ? null : parseFloat(form.costo_materiali),
      costo_totale: form.costo_totale === '' ? null : parseFloat(form.costo_totale),
    });
  };

  if (!editing) {
    return (
      <div>
        <div style={styles.tabHeader}>
          <h3 style={{ margin: 0 }}>Informazioni intervento</h3>
          <button onClick={() => setEditing(true)} style={styles.editBtn}>
            <Edit2 size={14} /> Modifica
          </button>
        </div>

        <div style={styles.infoGrid}>
          <InfoRow label="Descrizione" value={intervento.descrizione || '—'} full />
          <InfoRow label="Tipo problema" value={intervento.tipo_problema || '—'} />
          <InfoRow label="Priorità" value={PRIORITA_STYLE[intervento.priorita]?.label || '—'} />
          <InfoRow label="Indirizzo" value={[intervento.indirizzo, intervento.citta, intervento.cap].filter(Boolean).join(', ') || '—'} full />
          <InfoRow label="Data appuntamento" value={fmtDataOra(intervento.data_appuntamento)} />
          <InfoRow label="Data inizio lavori" value={fmtDataOra(intervento.data_inizio)} />
          <InfoRow label="Data fine lavori" value={fmtDataOra(intervento.data_fine)} />
          <InfoRow label="Ore totali" value={intervento.ore_totali ? fmtDurataOre(intervento.ore_totali) : '—'} />
          <InfoRow label="Costo manodopera" value={intervento.costo_manodopera ? `€ ${parseFloat(intervento.costo_manodopera).toFixed(2)}` : '—'} />
          <InfoRow label="Costo materiali" value={intervento.costo_materiali ? `€ ${parseFloat(intervento.costo_materiali).toFixed(2)}` : '—'} />
          <InfoRow label="Costo totale" value={intervento.costo_totale ? `€ ${parseFloat(intervento.costo_totale).toFixed(2)}` : '—'} highlight />
          <InfoRow label="Note cliente" value={intervento.note_cliente || '—'} full />
          <InfoRow label="Note interne (non visibili al cliente)" value={intervento.note_interne || '—'} full warning />
        </div>
      </div>
    );
  }

  // Modalità modifica
  return (
    <div>
      <div style={styles.tabHeader}>
        <h3 style={{ margin: 0 }}>Modifica intervento</h3>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => setEditing(false)} style={styles.cancelBtn}>
            <X size={14} /> Annulla
          </button>
          <button onClick={save} disabled={updateMutation.isPending} style={styles.saveBtn}>
            <Save size={14} /> {updateMutation.isPending ? 'Salvataggio...' : 'Salva'}
          </button>
        </div>
      </div>

      <div style={styles.formGrid}>
        <div style={styles.field}>
          <label style={styles.label}>Titolo *</label>
          <input value={form.titolo} onChange={handle('titolo')} style={styles.input} />
        </div>

        <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
          <label style={styles.label}>Descrizione</label>
          <textarea value={form.descrizione} onChange={handle('descrizione')} style={{ ...styles.input, minHeight: '70px', fontFamily: 'inherit' }} />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Tipo problema</label>
          <input value={form.tipo_problema} onChange={handle('tipo_problema')} style={styles.input} />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Priorità</label>
          <select value={form.priorita} onChange={handle('priorita')} style={styles.input}>
            <option value="bassa">Bassa</option>
            <option value="normale">Normale</option>
            <option value="alta">Alta</option>
            <option value="urgente">Urgente</option>
          </select>
        </div>

        <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
          <label style={styles.label}>Indirizzo</label>
          <input value={form.indirizzo} onChange={handle('indirizzo')} style={styles.input} />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Città</label>
          <input value={form.citta} onChange={handle('citta')} style={styles.input} />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>CAP</label>
          <input value={form.cap} onChange={handle('cap')} style={styles.input} />
        </div>

        <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
          <label style={styles.label}>Data e ora appuntamento</label>
          <input type="datetime-local" value={form.data_appuntamento} onChange={handle('data_appuntamento')} style={styles.input} />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Costo manodopera (€)</label>
          <input type="number" step="0.01" min="0" value={form.costo_manodopera} onChange={handle('costo_manodopera')} style={styles.input} />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Costo materiali (€)</label>
          <input type="number" step="0.01" min="0" value={form.costo_materiali} onChange={handle('costo_materiali')} style={styles.input} />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Costo totale (€)</label>
          <input type="number" step="0.01" min="0" value={form.costo_totale} onChange={handle('costo_totale')} style={styles.input} />
        </div>

        <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
          <label style={styles.label}>Note per il cliente</label>
          <textarea value={form.note_cliente} onChange={handle('note_cliente')} style={{ ...styles.input, minHeight: '60px', fontFamily: 'inherit' }} />
        </div>
        <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
          <label style={styles.label}>Note interne (non visibili al cliente)</label>
          <textarea value={form.note_interne} onChange={handle('note_interne')} style={{ ...styles.input, minHeight: '60px', fontFamily: 'inherit' }} />
        </div>

        {error && <div style={{ ...styles.errorBox, gridColumn: '1 / -1' }}>{error}</div>}
      </div>
    </div>
  );
}

function InfoRow({ label, value, full, highlight, warning }) {
  return (
    <div style={{
      gridColumn: full ? '1 / -1' : undefined,
      padding: '0.75rem',
      backgroundColor: warning ? '#FFFBEB' : (highlight ? '#F0FDF4' : '#F9FAFB'),
      borderRadius: '8px',
      border: warning ? '1px solid #FCD34D' : 'none',
    }}>
      <div style={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{
        fontSize: highlight ? '1.1rem' : '0.95rem',
        fontWeight: highlight ? 700 : 400,
        color: highlight ? '#059669' : '#111827',
        marginTop: '0.25rem',
        whiteSpace: 'pre-wrap',
      }}>{value}</div>
    </div>
  );
}

// ============================================================
// TAB: Tecnici
// ============================================================
function TabTecnici({ intervento, onChanged }) {
  const [showAdd, setShowAdd] = useState(false);
  const [tecnicoToAdd, setTecnicoToAdd] = useState('');
  const [ruoloIntervento, setRuoloIntervento] = useState('principale');

  const { data: tecnici = [] } = useQuery({
    queryKey: ['utenti', 'tecnico'],
    queryFn: () => utentiAPI.list('tecnico'),
  });

  const assignMutation = useMutation({
    mutationFn: ({ tecnico_id, ruolo_intervento }) => interventiAPI.assignTecnico(intervento.id, tecnico_id, ruolo_intervento),
    onSuccess: () => {
      setShowAdd(false);
      setTecnicoToAdd('');
      setRuoloIntervento('principale');
      onChanged();
    },
  });

  const removeMutation = useMutation({
    mutationFn: (tecnicoId) => interventiAPI.removeTecnico(intervento.id, tecnicoId),
    onSuccess: onChanged,
  });

  // Scavalca, solo su questo intervento, il valore predefinito della scheda del tecnico
  const visibilitaMutation = useMutation({
    mutationFn: ({ tecnicoId, visibile }) => interventiAPI.setVisibilitaTecnico(intervento.id, tecnicoId, visibile),
    onSuccess: onChanged,
  });

  const tecniciAssegnati = intervento.tecnici || [];
  const idsAssegnati = new Set(tecniciAssegnati.map(t => t.id));
  const tecniciDisponibili = tecnici.filter(t => !idsAssegnati.has(t.id) && t.attivo);

  return (
    <div>
      <div style={styles.tabHeader}>
        <h3 style={{ margin: 0 }}>Tecnici assegnati ({tecniciAssegnati.length})</h3>
        {!showAdd && tecniciDisponibili.length > 0 && (
          <button onClick={() => setShowAdd(true)} style={styles.addBtnSmall}>
            <Plus size={14} /> Assegna tecnico
          </button>
        )}
      </div>

      {showAdd && (
        <div style={styles.addTecnicoForm}>
          <select
            value={tecnicoToAdd}
            onChange={(e) => setTecnicoToAdd(e.target.value)}
            style={{ ...styles.input, flex: 2 }}
          >
            <option value="">— Seleziona tecnico —</option>
            {tecniciDisponibili.map((t) => (
              <option key={t.id} value={t.id}>{t.nome} {t.cognome}</option>
            ))}
          </select>
          <select
            value={ruoloIntervento}
            onChange={(e) => setRuoloIntervento(e.target.value)}
            style={{ ...styles.input, flex: 1 }}
          >
            <option value="principale">Principale</option>
            <option value="aggiuntivo">Aggiuntivo</option>
          </select>
          <button
            onClick={() => tecnicoToAdd && assignMutation.mutate({ tecnico_id: tecnicoToAdd, ruolo_intervento: ruoloIntervento })}
            disabled={!tecnicoToAdd || assignMutation.isPending}
            style={styles.saveBtn}
          >
            <Save size={14} /> Assegna
          </button>
          <button onClick={() => { setShowAdd(false); setTecnicoToAdd(''); }} style={styles.cancelBtn}>
            <X size={14} />
          </button>
        </div>
      )}

      {tecniciAssegnati.length === 0 ? (
        <div style={styles.emptyTab}>
          <Wrench size={32} style={{ color: '#9CA3AF', marginBottom: '0.5rem' }} />
          <p>Nessun tecnico assegnato.</p>
          {!showAdd && (
            <button onClick={() => setShowAdd(true)} style={styles.addBtnSmall}>
              <Plus size={14} /> Assegna il primo
            </button>
          )}
        </div>
      ) : (
        <div style={styles.tecniciList}>
          {tecniciAssegnati.map((t) => (
            <div key={t.id} style={styles.tecnicoCard}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>{t.nome} {t.cognome}</div>
                <div style={{ fontSize: '0.85rem', color: '#6B7280', marginTop: '0.2rem' }}>
                  {t.telefono && <>📞 {t.telefono} · </>}
                  {t.email && <>✉️ {t.email}</>}
                </div>
              </div>
              <span style={{
                ...styles.ruoloBadge,
                backgroundColor: t.ruolo_intervento === 'principale' ? '#EDE9FE' : '#F3F4F6',
                color: t.ruolo_intervento === 'principale' ? '#5B21B6' : '#4B5563',
              }}>
                {t.ruolo_intervento}
              </span>
              <select
                value={t.scelta_intervento === null || t.scelta_intervento === undefined ? 'default' : String(t.scelta_intervento)}
                onChange={(e) => {
                  const v = e.target.value;
                  visibilitaMutation.mutate({ tecnicoId: t.id, visibile: v === 'default' ? null : v === 'true' });
                }}
                disabled={visibilitaMutation.isPending}
                style={styles.visibilitaSelect}
                title="Se il cliente vede il nome di questo tecnico su questo intervento"
              >
                <option value="default">
                  Come da scheda ({t.tecnico_esterno ? 'nascosto' : 'visibile'})
                </option>
                <option value="true">Visibile al cliente</option>
                <option value="false">Nascosto al cliente</option>
              </select>
              <button
                onClick={() => { if (window.confirm(`Rimuovere ${t.nome} ${t.cognome} dall'intervento?`)) removeMutation.mutate(t.id); }}
                style={{ ...styles.iconBtnDanger }}
                title="Rimuovi tecnico"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// TAB: Materiali (read-only, popolato dai tecnici)
// ============================================================
function TabMateriali({ intervento, onChanged }) {
  const materiali = intervento.materiali || [];
  const totale = materiali.reduce((acc, m) => acc + (parseFloat(m.prezzo_totale) || 0), 0);
  const [inGiustificativo, setInGiustificativo] = useState(null);

  return (
    <div>
      <div style={styles.tabHeader}>
        <h3 style={{ margin: 0 }}>Materiali utilizzati ({materiali.length})</h3>
        {totale > 0 && <div style={styles.totaleMateriali}>Totale: € {totale.toFixed(2)}</div>}
      </div>

      {materiali.length === 0 ? (
        <div style={styles.emptyTab}>
          <Package size={32} style={{ color: '#9CA3AF', marginBottom: '0.5rem' }} />
          <p>Nessun materiale registrato.</p>
          <small style={{ color: '#9CA3AF' }}>I tecnici aggiungeranno i materiali consumati durante l'intervento.</small>
        </div>
      ) : (
        <div style={styles.tableScroll}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.thead}>
              <th style={styles.th}>Codice</th>
              <th style={styles.th}>Materiale</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Quantità</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Prezzo unit.</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Totale</th>
              <th style={styles.th}>Giustificativo</th>
            </tr>
          </thead>
          <tbody>
            {materiali.map((m) => (
              <tr key={m.id} style={styles.tr}>
                <td style={styles.td}><code style={styles.codeSm}>{m.codice || '—'}</code></td>
                <td style={styles.td}>
                  <strong>{m.materiale_nome}</strong>
                  {m.fuori_magazzino && <span style={styles.fuoriMagazzino}>comprato fuori</span>}
                </td>
                <td style={{ ...styles.td, textAlign: 'right' }}>{parseFloat(m.quantita)} {m.unita_misura}</td>
                <td style={{ ...styles.td, textAlign: 'right' }}>€ {parseFloat(m.prezzo_unitario || 0).toFixed(2)}</td>
                <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600 }}>€ {parseFloat(m.prezzo_totale || 0).toFixed(2)}</td>
                <td style={styles.td}>
                  {/* Il materiale di magazzino non ha giustificativo: e' roba nostra,
                      comprata a monte. La colonna riguarda solo cio' che il tecnico
                      ha comprato fuori. */}
                  {!m.fuori_magazzino ? (
                    <span style={styles.dashSoft}>—</span>
                  ) : (
                    <button style={styles.giustBtn} onClick={() => setInGiustificativo(m)}>
                      <Receipt size={14} />
                      <span>
                        {m.fornitore_nome || 'fornitore mancante'}
                        {m.ricevuta_file_id ? ' · con ricevuta' : ' · senza ricevuta'}
                      </span>
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {inGiustificativo && (
        <GiustificativoModal
          intervento={intervento}
          riga={inGiustificativo}
          onClose={() => setInGiustificativo(null)}
          onSaved={() => { setInGiustificativo(null); onChanged && onChanged(); }}
        />
      )}
    </div>
  );
}

/*
 * Il giustificativo di una riga comprata fuori magazzino: da chi si e' comprato e
 * quale foto lo dimostra.
 *
 * La ricevuta si SCEGLIE fra i file gia' caricati sull'intervento, non si carica
 * da qui. Motivo tecnico preciso: il vincolo `files_appartenenza_check` della
 * migrazione 007 ammette che un file appartenga a un intervento oppure a un
 * apparecchio, e un terzo tipo di proprieta' lo violerebbe. Cosi' invece un solo
 * scontrino puo' giustificare piu' righe, che e' anche il caso reale di chi fa
 * una spesa sola per due lavori.
 */
function GiustificativoModal({ intervento, riga, onClose, onSaved }) {
  const [fornitoreId, setFornitoreId] = useState(riga.fornitore_id || '');
  const [ricevutaId, setRicevutaId] = useState(riga.ricevuta_file_id || '');
  const [errore, setErrore] = useState('');

  const { data: fornitori = [] } = useQuery({
    queryKey: ['fornitori', 'attivi'],
    queryFn: () => fornitoriAPI.list({ attivo: 'true' }),
  });

  const files = intervento.files || [];

  const salva = useMutation({
    mutationFn: () => interventiAPI.aggiornaMateriale(intervento.id, riga.id, {
      fornitore_id: fornitoreId || null,
      ricevuta_file_id: ricevutaId || null,
    }),
    onSuccess: onSaved,
    onError: (e) => setErrore(e.response?.data?.error || 'Errore nel salvataggio'),
  });

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalG} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeaderG}>
          <h2 style={styles.modalTitleG}>{riga.materiale_nome}</h2>
          <button type="button" style={styles.iconBtnG} onClick={onClose}><X size={18} /></button>
        </div>

        <div style={styles.modalBodyG}>
          <label style={styles.labelG}>Comprato da</label>
          <select style={styles.inputG} value={fornitoreId} onChange={(e) => setFornitoreId(e.target.value)}>
            <option value="">— nessun fornitore —</option>
            {fornitori.map((f) => (
              <option key={f.id} value={f.id}>
                {f.denominazione}{f.partita_iva ? ` (${f.partita_iva})` : ''}
              </option>
            ))}
          </select>
          <div style={styles.hintG}>
            Se il negozio non c'e', si aggiunge dalla sezione Fornitori.
          </div>

          <label style={styles.labelG}>Ricevuta</label>
          {files.length === 0 ? (
            <div style={styles.hintG}>
              Non c'e' ancora nessun file su questo intervento. Carica la foto dello
              scontrino dalla scheda Foto/Files, poi torna qui a collegarla.
            </div>
          ) : (
            <>
              <select style={styles.inputG} value={ricevutaId} onChange={(e) => setRicevutaId(e.target.value)}>
                <option value="">— nessuna ricevuta —</option>
                {files.map((f) => (
                  <option key={f.id} value={f.id}>{f.nome_originale || f.nome_file}</option>
                ))}
              </select>
              <div style={styles.hintG}>
                Si sceglie fra i file gia' caricati sull'intervento. Lo stesso scontrino
                puo' giustificare piu' righe.
              </div>
            </>
          )}

          {errore && <div style={styles.erroreG}>{errore}</div>}
        </div>

        <div style={styles.modalActionsG}>
          <button type="button" style={styles.secondaryBtnG} onClick={onClose}>Annulla</button>
          <button type="button" style={styles.primaryBtnG} onClick={() => salva.mutate()} disabled={salva.isPending}>
            {salva.isPending ? 'Salvataggio...' : 'Salva'}
          </button>
        </div>
      </div>
    </div>
  );
}

function TabFiles({ intervento }) {
  const files = intervento.files || [];

  return (
    <div>
      <div style={styles.tabHeader}>
        <h3 style={{ margin: 0 }}>Foto e documenti ({files.length})</h3>
      </div>

      {files.length === 0 ? (
        <div style={styles.emptyTab}>
          <Image size={32} style={{ color: '#9CA3AF', marginBottom: '0.5rem' }} />
          <p>Nessun file caricato.</p>
          <small style={{ color: '#9CA3AF' }}>I tecnici potranno caricare foto e documenti dell'intervento.</small>
        </div>
      ) : (
        <div style={styles.filesGrid}>
          {files.map((f) => {
            const isImage = f.mime_type?.startsWith('image/');
            return (
              <div key={f.id} style={styles.fileCard}>
                {isImage ? (
                  <img src={`${process.env.REACT_APP_UPLOADS_URL || ''}/${f.nome_file}`} alt={f.nome_originale} style={styles.fileImg} />
                ) : (
                  <div style={styles.fileIcon}><FileText size={32} color="#6B7280" /></div>
                )}
                <div style={styles.fileMeta}>
                  <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{f.nome_originale}</div>
                  <div style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '0.2rem' }}>
                    {f.caricato_da_nome} · {relativeTime(f.created_at)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================
// TAB: Sessioni di lavoro
// L'admin puo' chiudere un timer ancora aperto e correggere una sessione gia'
// chiusa; ogni intervento resta nella cronologia dell'intervento.
// ============================================================
function TabSessioni({ intervento, onChanged }) {
  const sessioni = intervento.sessioni || [];
  const secondiTot = secondiTotaliSessioni(sessioni);
  const [inLavorazione, setInLavorazione] = useState(null); // { sessione, modo }

  return (
    <div>
      <div style={styles.tabHeader}>
        <h3 style={{ margin: 0 }}>Sessioni di lavoro ({sessioni.length})</h3>
        {secondiTot > 0 && <div style={styles.totaleMateriali}>Totale ore: {fmtDurata(secondiTot)}</div>}
      </div>

      {inLavorazione && (
        <SessioneAdminModal
          sessione={inLavorazione.sessione}
          modo={inLavorazione.modo}
          onClose={() => setInLavorazione(null)}
          onDone={() => { setInLavorazione(null); onChanged(); }}
        />
      )}

      {sessioni.length === 0 ? (
        <div style={styles.emptyTab}>
          <Clock size={32} style={{ color: '#9CA3AF', marginBottom: '0.5rem' }} />
          <p>Nessuna sessione di lavoro registrata.</p>
          <small style={{ color: '#9CA3AF' }}>I tecnici useranno il timer per tracciare ore di lavoro.</small>
        </div>
      ) : (
        <div style={styles.tableScroll}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.thead}>
              <th style={styles.th}>Tecnico</th>
              <th style={styles.th}>Inizio</th>
              <th style={styles.th}>Fine</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Durata</th>
              <th style={styles.th}>Lavoro svolto</th>
              <th style={styles.th}>Note</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {sessioni.map((s) => (
              <tr key={s.id} style={styles.tr}>
                <td style={styles.td}>{s.tecnico_nome} {s.tecnico_cognome}</td>
                <td style={styles.td}>{fmtDataOra(s.inizio)}</td>
                <td style={styles.td}>{s.fine ? fmtDataOra(s.fine) : <span style={{ color: '#059669', fontWeight: 600 }}>In corso</span>}</td>
                <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600 }}>{s.fine ? fmtDurata(secondiSessione(s)) : '—'}</td>
                <td style={{ ...styles.td, minWidth: '220px', whiteSpace: 'pre-wrap' }}>{s.dichiarazione || '—'}</td>
                <td style={{ ...styles.td, whiteSpace: 'pre-wrap' }}>{s.note || '—'}</td>
                <td style={{ ...styles.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button
                    onClick={() => setInLavorazione({ sessione: { ...s, intervento_id: intervento.id }, modo: s.fine ? 'modifica' : 'termina' })}
                    style={styles.azioneSessione}
                  >
                    {s.fine ? 'Correggi' : 'Chiudi'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

// ============================================================
// TAB: Log / Cronologia
// ============================================================
function TabLog({ intervento }) {
  const log = intervento.log || [];

  return (
    <div>
      <div style={styles.tabHeader}>
        <h3 style={{ margin: 0 }}>Cronologia ({log.length})</h3>
      </div>

      {log.length === 0 ? (
        <div style={styles.emptyTab}>
          <History size={32} style={{ color: '#9CA3AF', marginBottom: '0.5rem' }} />
          <p>Nessun evento registrato.</p>
        </div>
      ) : (
        <div style={styles.logList}>
          {log.map((l) => (
            <div key={l.id} style={styles.logItem}>
              <div style={styles.logTime}>
                <div style={{ fontSize: '0.78rem', color: '#6B7280' }}>{relativeTime(l.created_at)}</div>
                <div style={{ fontSize: '0.7rem', color: '#9CA3AF', marginTop: '0.1rem' }}>{fmtDataOra(l.created_at)}</div>
              </div>
              <div style={styles.logContent}>
                <div style={{ fontWeight: 600, fontSize: '0.92rem' }}>{actionLabel(l.azione)}</div>
                {l.stato_precedente && l.stato_nuovo && (
                  <div style={{ fontSize: '0.85rem', color: '#6B7280', marginTop: '0.2rem' }}>
                    <span style={styles.logStatoBadge}>{STATO_LABELS[l.stato_precedente] || l.stato_precedente}</span>
                    {' → '}
                    <span style={styles.logStatoBadge}>{STATO_LABELS[l.stato_nuovo] || l.stato_nuovo}</span>
                  </div>
                )}
                {l.dettagli && <div style={{ fontSize: '0.85rem', color: '#6B7280', marginTop: '0.25rem' }}>{l.dettagli}</div>}
                {(l.utente_nome || l.utente_cognome) && (
                  <div style={{ fontSize: '0.78rem', color: '#9CA3AF', marginTop: '0.3rem' }}>
                    da {l.utente_nome} {l.utente_cognome}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function actionLabel(action) {
  const labels = {
    creazione: 'Intervento creato',
    cambio_stato: 'Cambio stato',
    assegna_tecnico: 'Tecnico assegnato',
    rimuovi_tecnico: 'Tecnico rimosso',
    modifica: 'Intervento modificato',
    timer_avviato: 'Timer avviato',
    timer_annullato: 'Timer annullato',
    sessione_chiusa_da_admin: 'Timer chiuso dall\'amministratore',
    sessione_modificata: 'Sessione corretta',
    'fatturazione-registrata': 'Fattura di periodo registrata',
    'fatturazione-aggiornata': 'Fattura di periodo aggiornata',
    'fatturazione-eliminata': 'Fattura di periodo eliminata',
  };
  return labels[action] || action;
}

const styles = {
  fuoriMagazzino: { display: 'block', fontSize: '0.72rem', color: '#92400E', marginTop: '0.15rem' },
  dashSoft: { color: '#D1D5DB' },
  giustBtn: { display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.35rem 0.6rem', backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '0.78rem', color: '#374151', cursor: 'pointer', textAlign: 'left', minHeight: '34px' },
  modalG: { backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '460px', maxHeight: '90vh', overflow: 'auto', display: 'flex', flexDirection: 'column' },
  modalHeaderG: { padding: '1.1rem 1.25rem', borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' },
  modalTitleG: { margin: 0, fontSize: '1.02rem', fontWeight: 700 },
  modalBodyG: { padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.2rem' },
  modalActionsG: { padding: '1rem 1.25rem', borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'flex-end', gap: '0.6rem' },
  iconBtnG: { border: 'none', background: 'none', cursor: 'pointer', color: '#6B7280', display: 'flex', padding: '0.25rem' },
  labelG: { fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginTop: '0.7rem' },
  inputG: { width: '100%', padding: '0.6rem 0.7rem', border: '1px solid #E5E7EB', borderRadius: '10px', fontSize: '0.92rem', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', backgroundColor: 'white' },
  hintG: { fontSize: '0.78rem', color: '#6B7280', marginTop: '0.35rem', lineHeight: 1.5 },
  erroreG: { marginTop: '0.8rem', padding: '0.6rem 0.75rem', backgroundColor: '#FEE2E2', color: '#991B1B', borderRadius: '8px', fontSize: '0.85rem' },
  primaryBtnG: { padding: '0.55rem 1rem', backgroundColor: '#8B5CF6', color: 'white', border: 'none', borderRadius: '10px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', minHeight: '40px' },
  secondaryBtnG: { padding: '0.55rem 1rem', backgroundColor: '#F3F4F6', color: '#374151', border: 'none', borderRadius: '10px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', minHeight: '40px' },
  contoTitolo: { fontSize: '0.95rem', fontWeight: 700, margin: '0 0 0.6rem', color: '#111827' },
  contoTabella: { width: '100%', borderCollapse: 'collapse', marginBottom: '1.25rem' },
  contoTh: { padding: '0.5rem 0.7rem', textAlign: 'left', fontSize: '0.72rem', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.03em', backgroundColor: '#F9FAFB' },
  contoTr: { borderTop: '1px solid #F3F4F6' },
  contoTd: { padding: '0.6rem 0.7rem', fontSize: '0.88rem', verticalAlign: 'top' },
  tagEsterno: { display: 'block', fontSize: '0.72rem', color: '#92400E', marginTop: '0.15rem' },
  tagInterno: { display: 'block', fontSize: '0.72rem', color: '#6B7280', marginTop: '0.15rem' },
  totaliBox: { border: '1px solid #E5E7EB', borderRadius: '10px', padding: '0.85rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.45rem', maxWidth: '460px' },
  totaleRiga: { display: 'flex', justifyContent: 'space-between', gap: '1rem', fontSize: '0.9rem' },
  totaleFinale: { borderTop: '1px solid #E5E7EB', paddingTop: '0.5rem', marginTop: '0.2rem', fontWeight: 700, fontSize: '1.05rem' },
  notaMargine: { marginTop: '0.85rem', padding: '0.7rem 0.85rem', backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '10px', fontSize: '0.8rem', color: '#4B5563', lineHeight: 1.5, maxWidth: '620px' },
  avvisiBox: { display: 'flex', gap: '0.6rem', alignItems: 'flex-start', padding: '0.75rem 0.9rem', backgroundColor: '#FEF3C7', border: '1px solid #FDE68A', color: '#92400E', borderRadius: '10px', fontSize: '0.87rem', marginBottom: '1rem' },
  avvisiLista: { margin: '0.3rem 0 0', paddingLeft: '1.1rem' },
  diversoBox: { display: 'flex', gap: '0.6rem', alignItems: 'flex-start', padding: '0.75rem 0.9rem', backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE', color: '#1E40AF', borderRadius: '10px', fontSize: '0.87rem', marginBottom: '1rem', lineHeight: 1.5 },
  contoAzioni: { display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginTop: '1.25rem' },
  ricalcolaBtn: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.55rem 1rem', backgroundColor: '#8B5CF6', color: 'white', border: 'none', borderRadius: '10px', fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer', minHeight: '40px' },
  contoNota: { fontSize: '0.78rem', color: '#6B7280' },
  btnRegistro: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.45rem 0.85rem', backgroundColor: 'white', color: '#374151', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '0.84rem', fontWeight: 600, cursor: 'pointer' },
  filtroBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.1rem' },
  filtroUnita: { display: 'inline-flex', backgroundColor: '#F3F4F6', borderRadius: '10px', padding: '3px', flexWrap: 'wrap' },
  filtroBtn: { border: 'none', background: 'none', padding: '0.4rem 0.8rem', borderRadius: '8px', fontSize: '0.84rem', fontWeight: 600, color: '#4B5563', cursor: 'pointer', minHeight: '34px' },
  filtroBtnAttivo: { backgroundColor: 'white', color: '#7C3AED', boxShadow: '0 1px 2px rgba(0,0,0,0.08)' },
  filtroNav: { display: 'inline-flex', alignItems: 'center', gap: '0.35rem' },
  filtroFreccia: { border: '1px solid #E5E7EB', background: 'white', borderRadius: '8px', width: '34px', height: '34px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#374151' },
  filtroEtichetta: { fontSize: '0.88rem', fontWeight: 600, color: '#111827', minWidth: '150px', textAlign: 'center' },
  filtroOggi: { border: '1px solid #E5E7EB', background: 'white', borderRadius: '8px', padding: '0 0.7rem', height: '34px', fontSize: '0.8rem', fontWeight: 600, color: '#374151', cursor: 'pointer' },
  oreRiassunto: { display: 'flex', gap: '2rem', flexWrap: 'wrap', padding: '0.9rem 1rem', backgroundColor: '#F5F3FF', borderRadius: '10px', marginBottom: '1.25rem' },
  oreRiassuntoNum: { fontSize: '1.35rem', fontWeight: 800, color: '#5B21B6' },
  oreRiassuntoLabel: { fontSize: '0.78rem', color: '#6B7280', marginTop: '0.1rem' },
  barre: { display: 'flex', flexDirection: 'column', gap: '0.3rem', maxWidth: '560px' },
  barraRiga: { display: 'grid', gridTemplateColumns: '110px 1fr 70px', alignItems: 'center', gap: '0.6rem', fontSize: '0.82rem' },
  barraQuando: { color: '#4B5563', textTransform: 'capitalize' },
  barraTraccia: { height: '10px', backgroundColor: '#F3F4F6', borderRadius: '5px', overflow: 'hidden' },
  barraPiena: { display: 'block', height: '100%', backgroundColor: '#8B5CF6', borderRadius: '5px' },
  barraOre: { textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#111827', fontWeight: 600 },

  manutBox: { backgroundColor: 'white', borderRadius: '12px', padding: '1.1rem 1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', marginBottom: '1rem', borderLeft: '4px solid #8B5CF6' },
  manutHead: { display: 'flex', alignItems: 'center', gap: '0.45rem', marginBottom: '0.6rem' },
  manutTitle: { fontWeight: 700, fontSize: '0.92rem', color: '#8B5CF6' },
  manutBody: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' },
  manutProdotto: { fontWeight: 600, fontSize: '0.95rem' },
  manutMeta: { fontSize: '0.85rem', color: '#6B7280', marginTop: '0.2rem' },
  manutBtn: { display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.5rem 0.9rem', backgroundColor: '#F3E8FF', color: '#8B5CF6', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.83rem', whiteSpace: 'nowrap' },
  loading: { padding: '3rem', textAlign: 'center', color: '#6B7280' },
  backBtn: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 0.9rem', backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', cursor: 'pointer', marginBottom: '1rem', fontSize: '0.88rem' },
  errorCard: { backgroundColor: 'white', padding: '2rem', borderRadius: '12px', textAlign: 'center' },

  headerCard: { backgroundColor: 'white', padding: '1.5rem', borderRadius: '12px', marginBottom: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  headerTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' },
  badgesRow: { display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem', flexWrap: 'wrap' },
  statoBadge: { padding: '0.25rem 0.7rem', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 700 },
  prioBadge: { display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '0.2rem 0.6rem', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 600 },
  codice: { backgroundColor: '#F3F4F6', padding: '0.2rem 0.55rem', borderRadius: '4px', fontSize: '0.82rem', fontFamily: 'ui-monospace, monospace', color: '#374151' },
  headerTitle: { margin: 0, fontSize: '1.4rem', fontWeight: 700, color: '#111827' },
  tipoProblema: { fontSize: '0.88rem', color: '#6B7280', marginTop: '0.25rem' },
  statoSelector: { display: 'flex', flexDirection: 'column', gap: '0.3rem', minWidth: '180px' },
  statoLabel: { fontSize: '0.75rem', color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' },
  statoSelect: { padding: '0.5rem 0.75rem', border: '2px solid', borderRadius: '8px', fontSize: '0.92rem', fontWeight: 600, cursor: 'pointer', backgroundColor: 'white' },
  headerMeta: { display: 'flex', flexWrap: 'wrap', gap: '0.9rem 1.25rem', fontSize: '0.88rem', color: '#4B5563', paddingTop: '0.75rem', borderTop: '1px solid #F3F4F6' },
  internoBadge: { display: 'inline-flex', alignItems: 'center', backgroundColor: '#EDE9FE', color: '#5B21B6', padding: '0.2rem 0.6rem', borderRadius: '6px', fontWeight: 600, fontSize: '0.85rem' },

  tabsRow: { display: 'flex', gap: '0.25rem', marginBottom: '0', flexWrap: 'wrap', backgroundColor: 'white', padding: '0.5rem 0.5rem 0', borderRadius: '12px 12px 0 0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  tabBtn: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.65rem 1rem', backgroundColor: 'transparent', border: 'none', borderBottom: '3px solid transparent', cursor: 'pointer', fontSize: '0.92rem', fontWeight: 500, color: '#6B7280' },
  tabBtnActive: { color: ACCENT, borderBottomColor: ACCENT, fontWeight: 600 },
  tabBadge: { backgroundColor: '#F3F4F6', color: '#4B5563', padding: '0.1rem 0.5rem', borderRadius: '10px', fontSize: '0.72rem', fontWeight: 600 },

  tabContent: { backgroundColor: 'white', padding: '1.5rem', borderRadius: '0 0 12px 12px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  tabHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' },
  editBtn: { display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.45rem 0.85rem', backgroundColor: 'white', color: '#374151', border: '1px solid #D1D5DB', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500 },
  saveBtn: { display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.45rem 0.85rem', backgroundColor: ACCENT, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 },
  cancelBtn: { display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.45rem 0.85rem', backgroundColor: 'white', color: '#374151', border: '1px solid #D1D5DB', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem' },
  addBtnSmall: { display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.45rem 0.85rem', backgroundColor: ACCENT, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 },
  iconBtnDanger: { padding: '0.4rem', backgroundColor: 'transparent', border: '1px solid #FCA5A5', borderRadius: '6px', cursor: 'pointer', color: '#DC2626', display: 'inline-flex' },

  infoGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.75rem' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.85rem' },
  field: { display: 'flex', flexDirection: 'column', gap: '0.3rem' },
  label: { fontSize: '0.82rem', fontWeight: 600, color: '#374151' },
  input: { padding: '0.55rem 0.75rem', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', width: '100%' },
  errorBox: { backgroundColor: '#FEE2E2', color: '#991B1B', padding: '0.65rem', borderRadius: '8px', fontSize: '0.85rem' },

  emptyTab: { padding: '2.5rem 1rem', textAlign: 'center', color: '#6B7280', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' },

  addTecnicoForm: { display: 'flex', gap: '0.5rem', marginBottom: '1rem', padding: '0.85rem', backgroundColor: '#F9FAFB', borderRadius: '8px', flexWrap: 'wrap' },
  tecniciList: { display: 'flex', flexDirection: 'column', gap: '0.6rem' },
  tecnicoCard: { display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.85rem 1rem', backgroundColor: '#F9FAFB', borderRadius: '8px' },
  visibilitaSelect: { padding: '0.3rem 0.5rem', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '0.78rem', color: '#374151', backgroundColor: 'white', cursor: 'pointer', maxWidth: '210px' },
  ruoloBadge: { padding: '0.2rem 0.6rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600, textTransform: 'capitalize' },

  tableScroll: { width: '100%', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '560px' },
  thead: { backgroundColor: '#F9FAFB' },
  th: { padding: '0.7rem 0.85rem', textAlign: 'left', fontSize: '0.74rem', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em' },
  tr: { borderTop: '1px solid #F3F4F6' },
  td: { padding: '0.7rem 0.85rem', fontSize: '0.9rem', color: '#374151' },
  codeSm: { backgroundColor: '#F3F4F6', padding: '0.1rem 0.4rem', borderRadius: '4px', fontSize: '0.78rem', fontFamily: 'ui-monospace, monospace' },
  totaleMateriali: { fontWeight: 700, color: '#059669', fontSize: '1rem' },
  azioneSessione: { padding: '0.3rem 0.7rem', backgroundColor: 'white', color: ACCENT, border: `1px solid ${ACCENT}55`, borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem' },

  filesGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.85rem' },
  fileCard: { backgroundColor: '#F9FAFB', borderRadius: '8px', overflow: 'hidden' },
  fileImg: { width: '100%', height: '140px', objectFit: 'cover', display: 'block' },
  fileIcon: { height: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F3F4F6' },
  fileMeta: { padding: '0.6rem 0.75rem' },

  logList: { display: 'flex', flexDirection: 'column', gap: '0.4rem' },
  logItem: { display: 'flex', gap: '1rem', padding: '0.75rem', backgroundColor: '#F9FAFB', borderRadius: '8px' },
  logTime: { minWidth: '110px' },
  logContent: { flex: 1 },
  logStatoBadge: { backgroundColor: 'white', padding: '0.1rem 0.45rem', borderRadius: '4px', fontSize: '0.8rem', border: '1px solid #E5E7EB' },
};
